import { Box, Flex } from '@chakra-ui/react';
import { Formik, FormikProps, FormikValues } from 'formik';
import { isEqual, isFunction, size } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'ui-components';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { useBreakpointValue } from '~/utils-ts';

import Button from '../Button';
import { STATUS } from './StepperForm.constants';
import { createInitialStepState } from './StepperForm.helpers';
import {
  StepperFormOnStepSubmit,
  StepperFormOnStepValidate,
  StepperFormProps,
  StepperFormStepStateType,
} from './StepperForm.types';
import { StepperFormNav, StepperFormStep } from './components';

export const StepperForm: React.FC<StepperFormProps> = ({
  children,
  defaultActiveStepIndex = 0,
  flexProps = {},
  hideSubmit = false,
  isLinear = false,
  isSubmitAllOnStepEnabled = false,
  onSubmit,
  steps,
  textContinueButton = 'Continue',
  textSubmitButton = 'Submit',
  formikConfig,
  ...boxProps
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(
    defaultActiveStepIndex,
  );
  const [stepsState, setStepsState] = useState(
    createInitialStepState(steps, activeStepIndex, isLinear),
  );

  const [status, setStatus] = useState<STATUS>(STATUS.idle);
  const formRef = useRef<FormikProps<FormikValues>>(null);
  const isLastStep = activeStepIndex + 1 === steps.length;
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;
  const snackbar = useSnackbar();

  const resetForm = (index: number = defaultActiveStepIndex) => {
    setActiveStepIndex(index);
    setStepsState(createInitialStepState(steps, index, isLinear));
  };

  const getNextStep = () => {
    const nextStepIndex = activeStepIndex + 1;
    if (steps[nextStepIndex]) {
      return nextStepIndex;
    }
    return activeStepIndex;
  };

  const getStepValues = (lastStepValues: FormikValues) => {
    const values: FormikValues = stepsState.reduce(
      (acc, { values }) => ({
        ...acc,
        ...values,
      }),
      {},
    );
    return { ...values, ...lastStepValues };
  };

  const updateStepState = (
    stepIndex: number,
    updateSomeProps: Partial<StepperFormStepStateType>,
  ) => {
    const updatedStepsState = stepsState.map((stepState, index) =>
      stepIndex === index
        ? {
            ...stepState,
            ...updateSomeProps,
          }
        : stepState,
    );
    setStepsState(updatedStepsState);
  };

  const handleFormikFormSubmit = async (values: FormikValues) => {
    setStatus(STATUS.loading);
    onSubmit && (await onSubmit(values, steps, stepsState));
    setStatus(STATUS.idle);
    !isSubmitAllOnStepEnabled && setStatus(STATUS.complete);
  };

  const handleFormikStepSubmit: StepperFormOnStepSubmit = async (
    values,
    action,
  ) => {
    const { onSubmit: onStepSubmit } = steps[activeStepIndex];
    const nextStepIndex = getNextStep();
    setStatus(STATUS.loading);
    try {
      if (isSubmitAllOnStepEnabled) {
        const updatedValues = getStepValues(values);
        await handleFormikFormSubmit(updatedValues);
      } else {
        onStepSubmit && (await onStepSubmit(values, action));
      }

      if (!onSubmit || !isLastStep) {
        setStatus(STATUS.idle);
        action.resetForm();
      }

      updateStepState(activeStepIndex, {
        hasError: false,
        isCompleted: true,
        isDisabled: false,
        isValid: true,
        values,
      });

      if (!isSubmitAllOnStepEnabled && nextStepIndex !== activeStepIndex) {
        setActiveStepIndex(nextStepIndex);
      }

      if (onSubmit && isLastStep) {
        const updatedValues = getStepValues(values);
        handleFormikFormSubmit(updatedValues);
      }
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      updateStepState(activeStepIndex, {
        hasError: true,
        isDisabled: false,
        isValid: false,
        values,
      });
      setStatus(STATUS.error);
    }
  };

  const handleFormikStepValidate: StepperFormOnStepValidate = (values) => {
    const { onValidate: onStepValidate } = steps[activeStepIndex];
    const errors = onStepValidate(values);
    if (Object.keys(errors).length) {
      setStatus(STATUS.error);
      updateStepState(activeStepIndex, {
        hasError: true,
        isValid: false,
      });
    } else {
      setStatus(STATUS.idle);
      updateStepState(activeStepIndex, {
        hasError: false,
        isValid: true,
      });
    }
    return errors;
  };

  const handleGoToStep = (index: number) => {
    if (isSubmitAllOnStepEnabled && formRef.current) {
      const { values, initialValues } = formRef.current;
      const hasChanged = !isEqual(initialValues, values);
      if (hasChanged) {
        const hasError = !!size(steps[activeStepIndex].onValidate(values));
        updateStepState(activeStepIndex, {
          hasError: hasError,
          isDisabled: false,
          isValid: !hasError,
          values,
        });
      }
    }

    setActiveStepIndex(index);
  };

  useEffect(() => {
    const activeStepState = stepsState[activeStepIndex];
    if (activeStepState) {
      const updatedStepsState = stepsState.map((stepState, index) => {
        const isActive = index === activeStepIndex;
        return {
          ...stepState,
          isActive,
          isDisabled: !isActive && stepState.isDisabled,
        };
      });
      setStepsState(updatedStepsState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex]);

  return (
    <Formik
      enableReinitialize
      initialValues={stepsState[activeStepIndex].values}
      innerRef={formRef}
      onSubmit={handleFormikStepSubmit}
      validate={handleFormikStepValidate}
      {...formikConfig}
    >
      {(formikState) => (
        <Box {...boxProps}>
          <>
            <Flex
              flexDirection={{ base: 'column', md: 'row' }}
              minHeight={356}
              marginBottom={3}
              {...flexProps}
            >
              <StepperFormNav
                onClick={handleGoToStep}
                isLinear={isLinear}
                isMobile={isMobile}
                status={status}
                steps={steps}
                stepsState={stepsState}
              />
              <StepperFormStep
                flex={1}
                activeStepIndex={activeStepIndex}
                formikState={formikState}
                isLinear={isLinear}
                isMobile={isMobile}
                onBack={handleGoToStep}
                steps={steps}
              />
            </Flex>

            {!hideSubmit && (
              <Flex as="footer" alignItems="center" justifyContent="flex-end">
                <Button
                  type="submit"
                  isLoading={
                    status === STATUS.loading || formikState.isSubmitting
                  }
                  onClick={formikState.submitForm}
                >
                  {isLastStep ? textSubmitButton : textContinueButton}
                </Button>
              </Flex>
            )}

            {isFunction(children)
              ? children({
                  activeStepIndex,
                  formikState,
                  isLastStep,
                  resetForm,
                  setActiveStepIndex,
                  setStatus,
                  status,
                  stepsState,
                })
              : children}
          </>
        </Box>
      )}
    </Formik>
  );
};
